import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { operations, selectors } from 'store/curriculumCourseManager'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import {
  ProgramToCoursesMap,
  getProgramKey,
} from 'layers/content/v2/hooks/useCourseManager'
import { ERROR_TYPES } from 'layers/content/constants'
import { transformCurriculumCourseTrees } from 'layers/content/v3/transforms/transformCurriculumCourseTree'

/**
 * Resolves the active siteId to use for a curriculum-service singleSite
 * fetch. Preference order:
 *   1. caller-provided `siteId` (typically `currentSite.siteId` injected by
 *      `withSiteContext`)
 *   2. first site in the program-filtered `userContext.sites` (the
 *      `useUserContext` hook already narrows sites to the active program,
 *      so the first entry is an appropriate fallback)
 */
function resolveSiteId({ siteId, userContext }) {
  if (siteId !== null && siteId !== undefined) return siteId
  return userContext?.sites?.[0]?.siteId ?? null
}

/**
 * v3 replacement for `useCourseManager` that fetches course trees from the
 * curriculum service `POST /v1/audienced-course-versions` endpoint instead of
 * the legacy `@cfc/course-manager-sdk`.
 *
 * Reuses the existing program → courses mapping (`ProgramToCoursesMap`) and
 * program → contentGroupCode mapping (`getProgramKey`) from
 * `useCourseManager` so both code paths stay in sync.
 *
 * Returns a value-shape compatible drop-in for `useCourseManager` so the
 * matching v3 HOC can hand off `courseManager` props with no consumer
 * changes.
 *
 * Inputs:
 *   - `program`               (required) — value from the `programs` enum
 *   - `siteId`                (optional) — overrides resolution from userContext
 *   - `overrideHsPreferences` (optional) — accepted for API parity; the
 *     curriculum service public endpoint does not yet support preference
 *     overrides, so this value is currently ignored. The flag-off path
 *     continues to honor it.
 *
 * Returns:
 *   { courseTrees, loading, error, isUserContextLoading }
 */
export function useCurriculumCourseTrees(options) {
  const { program, siteId: explicitSiteId } = options || {}

  const dispatch = useDispatch()
  const userContext = useUserContext()

  const isUserContextLoading = useSelector(
    state => !!state?.userContextManager?.isFetching,
  )

  const isUserContextValid =
    !isUserContextLoading &&
    !!userContext &&
    Array.isArray(userContext?.sites) &&
    userContext.sites.length > 0

  const { contentGroupCode, courseCodes } = useMemo(() => {
    const programConfig = ProgramToCoursesMap?.[program]
    return {
      contentGroupCode: getProgramKey(program),
      courseCodes: programConfig?.courses ?? [],
    }
  }, [program])

  const siteId = useMemo(
    () => resolveSiteId({ siteId: explicitSiteId, userContext }),
    [explicitSiteId, userContext],
  )

  const hasProgramAccess = useMemo(() => {
    if (!isUserContextValid) return false
    if (!contentGroupCode) return false
    return userContext.sites.some(site => !!site?.programs?.[contentGroupCode])
  }, [isUserContextValid, contentGroupCode, userContext])

  const hasDeps =
    !!contentGroupCode &&
    courseCodes.length > 0 &&
    siteId !== null &&
    siteId !== undefined &&
    isUserContextValid &&
    hasProgramAccess

  const selection = useSelector(
    selectors.selectCurriculumCourseTrees(
      contentGroupCode,
      courseCodes,
      siteId,
    ),
  )

  useEffect(() => {
    if (!hasDeps) return
    for (const courseCode of courseCodes) {
      const existing = selection.courseTrees?.[courseCode]
      if (existing) continue
      dispatch(
        operations.fetchCurriculumCourseTree({
          contentGroupCode,
          courseCode,
          siteId,
        }),
      )
    }
    // We intentionally exclude `selection` from deps to avoid refetching
    // immediately after the slice updates; per-course existence is checked
    // each pass against the current cached map.
  }, [hasDeps, contentGroupCode, courseCodes, siteId, dispatch])

  const courseTrees = useMemo(
    () => transformCurriculumCourseTrees(selection.courseTrees),
    [selection.courseTrees],
  )

  const error = useMemo(() => {
    if (!selection.error) return null
    return {
      message: selection.error.message,
      errorType: selection.error.errorType || ERROR_TYPES.courseManagerError,
    }
  }, [selection.error])

  return {
    courseTrees,
    loading: !!selection.loading,
    error,
    isUserContextLoading,
  }
}
